export const en = {
  // App
  appName: "DevPulse",

  // Navigation
  navDashboard: "Dashboard",
  navContributors: "Contributors",
  navTeams: "Teams",
  navRepositories: "Repositories",

  // Views
  viewOrganization: "Organization",
  viewGroup: "Group",
  viewIndividual: "Individual",
  viewSelectPlaceholder: "Select View",

  // Common
  loading: "Loading data...",
  authenticating: "Authenticating...",
  loadingError: "Data Loading Error",
  authError: "Authentication Error",
  retry: "Retry",
  tryAgain: "Try Again",
  profile: "Profile",
  settings: "Settings",
  logout: "Logout",
  selectTimeRange: "Select time range",
  selectTeam: "Select a team...",
  selectDeveloper: "Select a developer...",

  // Time ranges
  timeLast7Days: "Last 7 days",
  timeLast30Days: "Last 30 days",
  timeAll: "All",

  // Dashboard titles
  dashboardOrganizationTitle: "Organization Dashboard",
  dashboardOrganizationSubtitle:
    "Overview of development metrics across all repositories and team members",
  dashboardTeamTitle: "Team Dashboard",
  dashboardTeamSubtitle: "Development metrics for {team} team members",
  dashboardTeamNoData: "No teams configured. Please add team data to view team-specific metrics.",
  dashboardIndividualTitle: "Individual Dashboard",
  dashboardIndividualSubtitle: "Development metrics for {author}",
  dashboardIndividualNoData: "No authors found. Please load data to view individual metrics.",

  // Contributors page
  contributorsTitle: "Contributors",
  contributorsSubtitle: "Analyze contributor performance and activity",
  contributorsNoData: "No contributor data available for the selected time range.",

  // Teams page
  teamsTitle: "Teams",
  teamsSubtitle: "Analyze team performance and activity",
  teamsNoData: "No team data available for the selected time range.",

  // Repositories page
  repositoriesTitle: "Repositories",
  repositoriesSubtitle: "Analyze repository performance and activity",
  repositoriesNoData: "No repository data available for the selected time range.",
  repositoriesScoreTitle: "Repository Commit Complexity Score",
  repositoriesScoreDescription: "Total complexity score of all commits per repository.",
  repositoriesPRsTitle: "Repository Pull Requests",
  repositoriesPRsDescription: "Number of merged pull requests per repository.",

  // DORA Metrics page
  doraMetricsTitle: "DORA Metrics",
  doraMetricsSubtitle:
    "DevOps Research and Assessment (DORA) metrics for measuring software delivery performance",
  doraNoDataTitle: "No DORA Metrics Data Available",
  doraNoDataDescription:
    "DORA metrics require releases, pull requests, and commit data. Please ensure data is collected and exported.",
  doraMissingDataTitle: "Missing Data for Complete DORA Metrics",
  doraMissingDataDescription:
    "The following data points are missing or incomplete for full DORA metrics calculation:",
  doraDataLimitationsTitle: "Data Limitations",
  doraDataLimitationsDescription:
    "The DORA metrics shown are calculated from available data, which has some limitations:",
  doraOverallPerformance: "Overall DORA Performance Level",
  doraOverallDescription:
    "Based on the four key metrics, your team's overall performance level is calculated according to DORA benchmarks.",
  doraDeploymentFrequency: "Deployment Frequency",
  doraLeadTime: "Lead Time for Changes",
  doraChangeFailureRate: "Change Failure Rate",
  doraTimeToRestore: "Time to Restore Service",
  doraPerMonth: "per month",
  doraDays: "days",
  doraHours: "hours",
  doraDeploymentsByMonth: "Deployments by Month",
  doraLeadTimeTrend: "Lead Time Trend",
  doraFailureRateTrend: "Failure Rate Trend",
  doraRestoreTimeTrend: "Restore Time Trend",
  doraDeploymentFrequencyDetail: "Deployment Frequency Over Time",
  doraDeploymentFrequencyDetailDescription: "Number of deployments per month",
  doraLeadTimeDetail: "Lead Time for Changes Over Time",
  doraLeadTimeDetailDescription: "Average time from PR creation to merge (in hours) per month",
  doraMethodologyTitle: "Methodology & Data Sources",
  doraDeploymentFrequencyMethodology:
    "Calculated from published releases (excluding drafts and pre-releases) in the releases database table.",
  doraLeadTimeMethodology:
    "Calculated as the time between pull request creation and merge, measured from the pull_requests table.",
  doraChangeFailureRateMethodology:
    "Estimated by detecting bugfix or revert commits within 7 days after a release. This is an approximation since we don't have explicit deployment failure tracking.",
  doraTimeToRestoreMethodology:
    "Estimated as the time between a release and the first bugfix/revert commit. This is an approximation since we don't have incident management data.",

  // DORA Performance Levels
  doraLevelElite: "ELITE",
  doraLevelHigh: "HIGH",
  doraLevelMedium: "MEDIUM",
  doraLevelLow: "LOW",

  // DORA Missing Data Points
  doraMissingReleases: "Releases data for deployment frequency",
  doraMissingPullRequests: "Pull Request data for lead time calculation",
  doraMissingCommits: "Commit data for failure analysis",
  doraMissingDeployments: "Deployment data (Phase 2) - using releases as proxy",
  doraMissingWorkflowRuns: "CI/CD workflow data (Phase 2) - limited pipeline visibility",
  doraMissingIncidents: "Incident tracking data (Phase 3) - using bugfix commits as proxy",

  error: "Error",

  // Tabs
  tabPerformance: "Performance",
  tabScore: "Score",
  tabPRs: "PRs",
  tabIssues: "Issues",
  tabActivity: "Activity",
  tabReleases: "Releases",

  // Activity
  recentActivity: "Recent Activity",

  // Releases tab
  releasesTitle: "Releases (Last 12 Weeks)",
  releasesDescription: "Total number of releases published across all repositories",
  releasesChartTitle: "Releases by Repository (Last 6 Weeks)",
  releasesChartDescription: "Weekly release activity per repository",

  // Performance tab
  performanceScoreTitle: "Overall Performance Score (0-100)",
  performanceScoreDescription:
    "A comprehensive metric combining code quality (30%), delivery throughput (25%), code impact (20%), problem-solving (15%), and work versatility (10%).",
  performanceScoreAverage: "Average: {value}",
  performanceScoreComparison:
    " — The percentage shows how each contributor compares to this average.",

  performanceScoreTeamTitle: "Overall Team Performance Score (0-100)",
  performanceScoreTeamDescription:
    "Aggregated performance score combining all team members' contributions across code quality, delivery throughput, code impact, problem-solving, and work versatility.",

  // Score tab
  scoreTitle: "Commit Complexity Score",
  scoreDescription:
    "Measures code complexity based on files changed, lines modified, function changes, and cyclomatic complexity. Higher scores indicate more challenging and impactful contributions.",
  scoreAverage: "Average: {value} points",
  scoreComparison: " — The percentage shows how each contributor compares to this average.",

  scoreTeamTitle: "Team Commit Complexity Score",
  scoreTeamDescription:
    "Combined complexity score from all team members' commits, measuring technical challenge and impact of code changes. Higher scores indicate more complex and impactful work.",

  // PRs tab
  prsTitle: "Merged Pull Requests",
  prsDescription:
    "Total number of successfully merged pull requests. This metric reflects code review participation, collaboration, and successful delivery of features and fixes.",
  prsAverage: "Average: {value} PRs",
  prsComparison: " — The percentage shows how each contributor compares to this average.",

  prsTeamTitle: "Team Merged Pull Requests",
  prsTeamDescription:
    "Total merged pull requests from all team members. This reflects team collaboration, code review participation, and successful delivery of features and fixes.",

  // Performance tab - Insight cards
  insightMergedPRs: "Merged PRs",
  insightPRThroughput: "PR Throughput",
  insightFeatureCommits: "Feature Commits",
  insightFeatures: "Features",
  insightBugfixCommits: "Bugfix Commits",
  insightBugfixes: "Bugfixes",
  insightMaintenanceCommits: "Maintenance Commits",
  insightMaintenance: "Maintenance",

  // Chart titles
  chartCommitsByClassification: "Commits by Classification",
  chartCommitsBySize: "Commits by Size",
  chartCommitScoresByWeek: "Commit Scores by Week",
  chartCommitScoresByWeekDesc: "Total commit complexity scores grouped by week and stacked by size",
  chartCommitScoresByClassificationWeek: "Commit Scores by Classification",
  chartCommitScoresByClassificationWeekDesc:
    "Total commit complexity scores grouped by week and stacked by classification",
  chartPRsMerged: "PRs Merged This Month",
  chartPRsMergedByWeek: "PRs Merged by Week",
  chartPRsMergedByWeekDesc: "Number of merged pull requests per week for the last 6 weeks",
  chartCodeChanges: "Code Changes",
  chartCodeChangesByWeek: "Code Changes by Week",
  chartCodeChangesByWeekDesc: "Lines added and removed from merged PRs per week",
  chartIssuesCreated: "Issues Created This Month",
  chartIssuesClosed: "Issues Closed This Month",
  chartActivityTitle: "Contribution Activity",
  chartActivityDescription:
    "Organization-wide activity including commits, pull requests, and issues over the last year",
  chartDetails: "Details",

  // Chart labels
  linesAdded: "Lines Added",
  linesRemoved: "Lines Removed",

  // Language selector
  languageLabel: "Language",
  languageEnglish: "English",
  languageGerman: "Deutsch",
  languageSpanish: "Español",
  languageChinese: "中文",

  // DataTable
  dataTableFilters: "Filters",
  dataTableFilterBy: "Filter by:",
  dataTableNoResults: "No results.",
  dataTableRowsPerPage: "Rows per page:",
  dataTablePage: "Page",
  dataTableOf: "of",
  dataTableTotalRows: "total rows",
  dataTableSearch: "Search...",

  // Charts
  chartTrend: "Trend",
  chartDistribution: "Distribution",
  chartDate: "Date",

  // Quick insights
  quickInsightDate: "Last 7 days",
  quickInsightVsLastWeek: "vs {count} last week",
  quickInsightVsLastMonth: "vs {count} last month",

  // Data table column headers
  tableColumnTitle: "Title",
  tableColumnMessage: "Message",
  tableColumnAuthor: "Author",
  tableColumnDate: "Date",
  tableColumnCreated: "Created",
  tableColumnScore: "Score",
  tableColumnFiles: "Files",
  tableColumnAdditions: "+",
  tableColumnDeletions: "-",

  // Chart descriptions
  chartCommitsByClassificationDesc:
    "Number of commits grouped by classification (commit type) and stacked by complexity size",
  chartCommitsBySizeDesc:
    "Number of commits grouped by complexity size and stacked by classification (commit type)",
  chartCodeChangesDesc: "Lines added and removed from merged PRs",
  chartPRsMergedDesc: "{count} PRs merged (vs {prevCount} last month)",
  chartIssuesCreatedDesc: "{count} issues created (vs {prevCount} last month)",
  chartIssuesClosedDesc: "{count} issues closed (vs {prevCount} last month)",
}

export type TranslationKeys = typeof en
